/**
 * Ticket API Client — mobile app API functions for the ticketing system.
 * Uses authenticated requests (JWT token from auth context).
 */

import { getApiBase } from "@/lib/api-env";
import { getAccessToken, refreshAccessToken } from "@/lib/auth";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getAccessToken();

    const headers: Record<string, string> = {
        ...(options?.body != null ? { "Content-Type": "application/json" } : {}),
        ...(options?.headers as Record<string, string>)
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    const res = await fetch(`${getApiBase()}${path}`, { ...options, headers });

    if (res.status === 401 && token) {
        const newToken = await refreshAccessToken();
        if (newToken) {
            headers["Authorization"] = `Bearer ${newToken}`;
            const retryRes = await fetch(`${getApiBase()}${path}`, { ...options, headers });
            if (!retryRes.ok) {
                const body = await retryRes.json().catch(() => ({}));
                throw new Error(
                    (typeof body.error === "string" ? body.error : body.error?.message) ||
                        `Request failed: ${retryRes.status}`
                );
            }
            return retryRes.json();
        }
        // Refresh returned null — a transient failure (network/timeout/5xx/429).
        // performRefresh now owns the logout-on-401 decision, so we must NOT log
        // the nurse out here. Surface a retryable error instead.
        throw new Error("Couldn't refresh your session. Please try again.");
    }

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
            (typeof body.error === "string" ? body.error : body.error?.message) ||
                `Request failed: ${res.status}`
        );
    }

    return res.json();
}

// ─── Types ───────────────────────────────────────────

/**
 * The ticket fields the API sends the app, on the Support list and the chat
 * screen alike (`RnTicket` in the API's ticket.service.ts). Nothing else of the
 * ticket reaches the nurse: not the escalation reason, its patient or agency.
 */
export interface TicketHeader {
    id: number;
    visitId: number | null;
    channel: "in_app_chat" | "sms" | "email" | "phone_call";
    status: "open" | "in_progress" | "resolved" | "closed";
    subject: string | null;
    updatedAt: string;
}

/** A row of the Support list. */
export interface Ticket extends TicketHeader {
    lastMessageContent: string | null;
    lastMessageAt: string | null;
    lastMessageSenderType: string | null;
    unreadCount: number;
    /** The visit the thread is about (paged feed only; null when the ticket has
     *  no visit, absent from the older unpaged list and older APIs). */
    visit?: TicketVisitContext | null;
}

/** What the Messages list may say about a thread's visit (the API applies the
 *  name rule: the full name only when the visit is hers or her request is
 *  pending; first name and last initial when she held it before; initials
 *  otherwise). */
export interface TicketVisitContext {
    visitId: number;
    /** "Indira Imran", "Indira I." or initials ("II") when `patientNameIsInitials`. */
    patientName: string;
    patientNameForm: "full" | "first_last_initial" | "initials";
    patientNameIsInitials: boolean;
    /** Naive wall-clock start/end, Z-suffixed: its UTC digits ARE the local time. */
    startTime: string | null;
    endTime: string | null;
    /** Staffing window (real instants) for a visit with no time yet. */
    scheduledStart: string | null;
    scheduledDue: string | null;
    timezone: string;
    status: string;
    assignedToMe: boolean;
    wasAssignedToMe: boolean;
}

/** A chat message as the API sends it to the app (what `TicketChatBubble` draws). */
export interface TicketChatMessage {
    id: number;
    senderType: string;
    senderName: string | null;
    content: string | null;
    attachments: Attachment[] | null;
    createdAt: string;
}

export interface Attachment {
    type: string;
    /** Canonical (unsigned) s3Url — what the server persists. */
    url: string;
    filename: string;
    size: number;
    mimeType: string;
    /** Short-lived presigned GET, added by the server on read paths. The bucket
     *  is private, so render this — never `url`. */
    viewUrl?: string;
}

/** The chat screen gets chat messages only: a ticket's calls and SMS are never sent. */
export interface TimelineItem {
    type: "chat_message";
    data: TicketChatMessage;
    createdAt: string;
}

export interface TicketTimeline {
    ticket: TicketHeader;
    items: TimelineItem[];
}

// ─── API Functions ───────────────────────────────────

export function fetchRnTickets(filters?: {
    status?: string;
    channel?: string;
}): Promise<{ data: Ticket[] }> {
    const params = new URLSearchParams();
    if (filters?.status) {
        params.set("status", filters.status);
    }
    if (filters?.channel) {
        params.set("channel", filters.channel);
    }
    const qs = params.toString();
    return request(`/rn/tickets${qs ? `?${qs}` : ""}`);
}

/**
 * One page of the Support list: chats with unread messages first, then the
 * rest, most recently updated first. Pass the previous page's `nextCursor` to
 * get the next one; `nextCursor` is null on the last page.
 */
export function fetchRnTicketFeed(params: {
    cursor?: string | null;
    limit?: number;
}): Promise<{ data: Ticket[]; nextCursor: string | null }> {
    const qs = new URLSearchParams();
    if (params.cursor) {
        qs.set("cursor", params.cursor);
    }
    if (params.limit) {
        qs.set("limit", String(params.limit));
    }
    const query = qs.toString();
    return request(`/rn/tickets/feed${query ? `?${query}` : ""}`);
}

export function fetchTicketTimeline(ticketId: number): Promise<{ data: TicketTimeline }> {
    return request(`/rn/tickets/${ticketId}`);
}

export function createRnTicket(params: {
    channel: string;
    subject?: string;
    visitId?: number;
    agencyId?: number;
}): Promise<{ data: TicketHeader }> {
    return request("/rn/tickets", {
        method: "POST",
        body: JSON.stringify({
            channel: params.channel,
            createdBy: "rn",
            subject: params.subject,
            visitId: params.visitId,
            agencyId: params.agencyId
        })
    });
}

export function markTicketRead(ticketId: number): Promise<{ data: { updated: number } }> {
    return request(`/rn/tickets/${ticketId}/read`, { method: "POST" });
}

export function sendTicketMessage(
    ticketId: number,
    content: string,
    attachments?: Attachment[]
): Promise<{ data: TicketChatMessage }> {
    return request(`/rn/tickets/${ticketId}/messages`, {
        method: "POST",
        body: JSON.stringify({
            channel: "in_app_chat",
            direction: "outbound",
            senderType: "rn",
            // An attachment-only message has no text. Omit rather than send ""
            // so the row stores NULL and downstream `content` checks stay honest.
            content: content === "" ? undefined : content,
            attachments: attachments && attachments.length > 0 ? attachments : undefined
        })
    });
}

/**
 * Step 1 of sending an attachment: ask for a presigned PUT. The nurse's device
 * then uploads the bytes straight to S3 (see `uploadFromUri`), and the returned
 * `s3Url` is what gets attached to the outgoing message.
 */
export function requestTicketAttachmentUploadUrl(
    ticketId: number,
    filename: string,
    contentType: string,
    size: number
): Promise<{ data: { uploadUrl: string; s3Url: string } }> {
    return request(`/rn/tickets/${ticketId}/attachments/upload-url`, {
        method: "POST",
        body: JSON.stringify({ filename, contentType, size })
    });
}

export function fetchUnreadCount(): Promise<{ unreadCount: number }> {
    return request("/rn/tickets/unread-count");
}
